import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { parent_routes } from "../../../router/all_routes";
import { createAnswerClaim, getClaimById } from "../../../../api/student";
import { toast } from "sonner";

interface Message {
  id: number;
  content: string;
  sender: "student" | "admin";
  author: string;
  date: string;
}

const STATUS_STYLES = {
  Envoyé: {
    icon: "ti ti-clock-hour-4",
    iconColor: "text-warning",
    badgeClass: "badge bg-warning-subtle text-warning",
    tone: "warning",
  },
  Traité: {
    icon: "ti ti-circle-check",
    iconColor: "text-success",
    badgeClass: "badge bg-success-subtle text-success",
    tone: "success",
  },
  Annulé: {
    icon: "ti ti-circle-x",
    iconColor: "text-danger",
    badgeClass: "badge bg-danger-subtle text-danger",
    tone: "danger",
  },
  Clôturé: {
    icon: "ti ti-lock",
    iconColor: "text-primary",
    badgeClass: "badge bg-primary-subtle text-primary",
    tone: "primary",
  },
};

const formatNote = (value: number): string =>
  String(Number(value).toFixed(2)).padStart(5, "0");

const getNoteTone = (value: number) => {
  if (value >= 10)
    return {
      color: "text-success",
      bg: "bg-success-subtle",
      border: "border-success",
      prog: "bg-success",
    };
  if (value >= 7)
    return {
      color: "text-warning",
      bg: "bg-warning-subtle",
      border: "border-warning",
      prog: "bg-warning",
    };
  return {
    color: "text-danger",
    bg: "bg-danger-subtle",
    border: "border-danger",
    prog: "bg-danger",
  };
};

const StudentClaimDetail = () => {
  const routes = parent_routes;

  const { id } = useParams(); // On récupère l'id de l'URL

  const [newMessage, setNewMessage] = useState("");
  const [claimDetail, setClaimDetail] = useState<any>(null);
  const [isSending, setIsSending] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(true);
  const [isMessagesOpen, setIsMessagesOpen] = useState(true);

  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [refreshKey, setRefreshKey] = useState(0);

  const rawStatus = claimDetail?.claim
    ?.statut_claim as keyof typeof STATUS_STYLES;
  const statusStyle = STATUS_STYLES[rawStatus] || STATUS_STYLES["Envoyé"];

  const tone = getNoteTone(claimDetail?.claim?.Note.value || 0);
  const prog = Math.min((claimDetail?.claim?.Note.value / 20) * 100, 100);

  const handleSend = async () => {
    const content = newMessage.trim();
    if (!content || isSending) return;

    setIsSending(true);
    try {
      // 👇 remplacer par ton appel API : await createClaimMessage(claim.id, content)
      const response = await createAnswerClaim({
        id_claim: claimDetail?.claim?.id,
        description: content,
      });

      const createdAnswer = response?.data || response;

      setClaimDetail((prev: any) => ({
        ...prev,
        answerClaim: [...(prev?.answerClaim || []), createdAnswer],
      }));

      setNewMessage("");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erreur lors de l'envoi");
    } finally {
      setIsSending(false);
    }
  };

  const formatMessageDate = (date: string) =>
    new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  const formatMessageTime = (date: string) =>
    new Date(date).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const threadMessages = [
    {
      id: `claim-${claimDetail?.claim?.id}`,
      description: claimDetail?.claim?.description,
      createdAt: claimDetail?.claim?.createdAt,
      isStudent: true,
      author: "Vous",
    },
    ...(claimDetail?.answerClaim || []).map((msg: any) => ({
      ...msg,
      isStudent: !!msg.id_studentProfil && !msg.UserProfil,
      author:
        !!msg.id_studentProfil && !msg.UserProfil
          ? "Vous"
          : `${msg.UserProfil?.firstname || ""} ${msg.UserProfil?.lastname || ""}`.trim() ||
            "Administration",
    })),
  ].filter((msg) => msg.description && msg.createdAt);

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!id) return;
    const fetchClaimDetails = async () => {
      try {
        setIsLoading(true);

        const response = await getClaimById(id);
        const claimData = response.data || [];

        setClaimDetail(claimData);

        // console.log("Claim", response);
      } catch (error: any) {
        // console.log("erreur de chargement de la Réclamation : ", error);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard",
        );

        // Option: reset les salles en cas d'erreur
        setClaimDetail(null);
      } finally {
        setIsLoading(false); //Les données ne sont plus en chargement
        setHasLoaded(true); //Les données ont été chargé avec succès
      }
    };

    fetchClaimDetails();
  }, [refreshKey, id]);

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="text-center">
              <span className="spinner-border text-primary" role="status" />
              <p className="mt-2 mb-0 text-muted">Chargement des détails...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!claimDetail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5 text-muted">
            <p className="mb-3">Impossible de charger cette réclamation.</p>

            <Link to={routes.claims} className="btn btn-primary">
              Retour aux réclamations
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* ── Page header ── */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Détail de la réclamation</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.claims}>Réclamations</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {claimDetail?.claim.Note.SubjectUE.Subject.label}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* ── Bandeau ── */}
        <div
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid var(--bs-border-color)",
            marginBottom: "1rem",
          }}
        >
          {/* Bande colorée en haut */}
          <div
            style={{
              height: 4,
              background:
                rawStatus === "Traité"
                  ? "#22c55e"
                  : rawStatus === "Annulé"
                    ? "#ef4444"
                    : rawStatus === "Clôturé"
                      ? "#2a61d0"
                      : "#eab308", // Envoyé
            }}
          />

          {/* Corps */}
          <div
            className="d-flex align-items-flex-start gap-3 p-3"
            style={{ background: "var(--bs-body-bg)" }}
          >
            {/* Icône ronde */}
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle"
              style={{
                width: 46,
                height: 46,
                background: `var(--bs-${statusStyle.tone}-bg-subtle)`,
                border: `1px solid var(--bs-${statusStyle.tone}-border-subtle)`,
                flexShrink: 0,
              }}
            >
              <i
                className={`${statusStyle.icon} fs-20 ${statusStyle.iconColor}`}
              />
            </div>

            {/* Textes */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                className={`mb-0 fs-11 fw-semibold text-uppercase `}
                style={{ letterSpacing: ".06em", marginBottom: 3 }}
              >
                {rawStatus === "Envoyé" ? "En cours d'analyse" : rawStatus}
              </p>
              <h5 className="mb-0 fw-semibold">
                {claimDetail?.claim?.Note?.SubjectUE?.Subject?.label}
              </h5>
              <p className="mb-0 text-muted fs-12">
                Evaluation :{" "}
                {claimDetail?.claim?.Note?.EvaluationLevel?.Evaluation?.label}
              </p>
            </div>
          </div>

          {/* Footer méta */}
          <div
            className="d-flex align-items-center flex-wrap gap-3 px-3 py-2"
            style={{
              borderTop: "1px solid var(--bs-border-color)",
              background: "var(--bs-secondary-bg)",
            }}
          >
            <span className="d-flex align-items-center gap-1 text-muted fs-12">
              <i className="ti ti-user fs-13" />
              Professeur :{" "}
              <strong className="text-body ms-1">
                {claimDetail?.claim?.Note?.SubjectUE?.TeacherProfil?.firstname}{" "}
                {claimDetail?.claim?.Note?.SubjectUE?.TeacherProfil?.lastname}
              </strong>
            </span>

            <span className="d-flex align-items-center gap-1 text-muted fs-12">
              <i className="ti ti-calendar-event fs-13" />
              Soumis le :{" "}
              <strong className="text-body ms-1">
                {new Date(claimDetail?.claim?.createdAt).toLocaleDateString(
                  "fr-FR",
                  {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  },
                )}
              </strong>
            </span>
          </div>
        </div>

        {/* ── Infos réclamation ── */}
        <div className="examd-card mb-3">
          <button
            type="button"
            className={`examd-section-head examd-section-head-button`}
            onClick={() => setIsInfoOpen((prev) => !prev)}
          >
            <h5>
              <i className="ti ti-info-circle" />
              Informations de la réclamation
            </h5>
            <i
              className={`ti ${isInfoOpen ? "ti-chevron-up" : "ti-chevron-down"}`}
            />
          </button>

          {isInfoOpen && (
            <>
              {/* ── Note réclamée ── */}
              <div className="d-flex align-items-center gap-3 p-3 border-bottom">
                <p
                  className="mb-3 text-muted fs-11 fw-semibold text-uppercase d-flex align-items-center gap-1"
                  style={{ letterSpacing: ".06em" }}
                >
                  <i className="ti ti-message-dots fs-13" />
                  Note réclamée
                </p>
                <br />
                <div className="d-flex align-items-center gap-2 flex-wrap flex-1">
                  <span
                    className={`fw-semibold fs-15 ${tone.color}`}
                    style={{ letterSpacing: "-0.02em" }}
                  >
                    {formatNote(claimDetail?.claim?.Note?.value || 0)}
                  </span>
                  <span className="text-muted fs-12">/ 20</span>

                  {/* Mini barre */}
                  <div className="flex-shrink-0" style={{ width: 80 }}>
                    <div
                      className="progress"
                      style={{
                        height: 4,
                        borderRadius: 99,
                        background: "var(--bs-secondary-bg)",
                      }}
                    >
                      <div
                        className={`progress-bar ${tone.prog}`}
                        style={{
                          width: `${prog}%`,
                          borderRadius: 99,
                          transition: "width .4s ease",
                        }}
                      />
                    </div>
                  </div>

                  <span
                    className={`badge ${tone.bg} ${tone.color} fs-11`}
                    style={{
                      borderRadius: 20,
                      padding: "2px 9px",
                      fontWeight: 500,
                    }}
                  >
                    {claimDetail?.claim?.Note?.value >= 10
                      ? "Validé"
                      : claimDetail?.claim?.Note?.value >= 7
                        ? "Passable"
                        : "En Session"}
                  </span>
                </div>
              </div>

              {/* ── Motif ── */}
              <div className="p-3 border-bottom">
                <p
                  className="mb-3 text-muted fs-11 fw-semibold text-uppercase d-flex align-items-center gap-1"
                  style={{ letterSpacing: ".06em" }}
                >
                  <i className="ti ti-message-dots fs-13" />
                  Motif de la réclamation
                </p>
                <div
                  className="position-relative rounded-2"
                  style={{
                    background: "var(--bs-secondary-bg)",
                    border: "1px solid var(--bs-border-color)",
                    padding: "13px 14px 13px 18px",
                  }}
                >
                  {/* Barre latérale gauche */}
                  <div
                    className="position-absolute top-0 start-0 bottom-0 rounded-start"
                    style={{ width: 3, background: "var(--bs-border-color)" }}
                  />
                  <p
                    className="mb-0"
                    style={{ fontSize: "0.85rem", lineHeight: 1.65 }}
                  >
                    {claimDetail?.claim?.description}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Messagerie ── */}
        <div className="examd-card mb-3">
          <button
            type="button"
            className="examd-section-head examd-section-head-button"
            onClick={() => setIsMessagesOpen((prev) => !prev)}
          >
            <h5>
              <i className="ti ti-messages" />
              Historique des échanges
            </h5>
            <i
              className={`ti ${isMessagesOpen ? "ti-chevron-up" : "ti-chevron-down"}`}
            />
          </button>

          {isMessagesOpen && (
            <>
              {/* Thread scrollable */}
              <div
                className="d-flex flex-column gap-2 p-3"
                style={{
                  maxHeight: 380,
                  overflowY: "auto",
                  borderBottom: "1px solid var(--bs-border-color)",
                }}
              >
                {threadMessages.map((msg: any, index: number) => {
                  const currentDate = formatMessageDate(msg.createdAt);
                  const previousDate =
                    index > 0
                      ? formatMessageDate(threadMessages[index - 1].createdAt)
                      : null;

                  const showDateSeparator =
                    index === 0 || currentDate !== previousDate;

                  return (
                    <div key={msg.id}>
                      {showDateSeparator && (
                        <div className="d-flex align-items-center justify-content-center my-2">
                          <span
                            className="px-3 py-1 text-muted fs-11"
                            style={{
                              background: "var(--bs-light)",
                              border: "1px solid var(--bs-border-color)",
                              borderRadius: 999,
                            }}
                          >
                            {currentDate}
                          </span>
                        </div>
                      )}

                      <div
                        className={`d-flex flex-column gap-1 ${msg.isStudent ? "align-items-end ms-auto" : "align-items-start me-auto"}`}
                        style={{
                          maxWidth: "85%",
                          marginBottom: "4px",
                        }}
                      >
                        <span className="text-muted fs-11">{msg.author}</span>

                        <div
                          className={
                            msg.isStudent ? "bg-primary-subtle" : "bg-light"
                          }
                          style={{
                            padding: "8px 12px",
                            borderRadius: msg.isStudent
                              ? "12px 12px 4px 12px"
                              : "12px 12px 12px 4px",
                            border: "1px solid",
                            borderColor: msg.isStudent
                              ? "var(--bs-primary-border-subtle)"
                              : "var(--bs-border-color)",
                            fontSize: "0.85rem",
                            lineHeight: 1.45,
                          }}
                        >
                          <div
                            className="d-inline-flex align-items-end gap-2 flex-wrap"
                            style={{ maxWidth: "100%" }}
                          >
                            <span
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {msg.description}
                            </span>

                            <span
                              className="text-muted fs-11 flex-shrink-0"
                              style={{ lineHeight: 1.2 }}
                            >
                              {formatMessageTime(msg.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentClaimDetail;
